import { useEffect, useState } from "react";
import { fetchSimulatorEnabled, simulateStep, type SimulationResult } from "../api/simulator";
import { messageOf } from "../lib/errors";
import { Button } from "./Button";

interface SimulatorPanelProps {
  pathway: "gig" | "finance";
  step: number;
  done: boolean;
  /** Reload the screen's data after a run so the real results show. */
  onRan: () => void;
}

/**
 * Local test drive. Fills in this step the way a careful participant would, through the same API the screen uses,
 * so every check and gate still runs. Only rendered when the API says it's running in local development.
 */
export function SimulatorPanel({ pathway, step, done, onRan }: SimulatorPanelProps) {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void fetchSimulatorEnabled().then((on) => live && setEnabled(on));
    return () => {
      live = false;
    };
  }, []);

  if (!enabled) return null;

  const run = async (complete: boolean) => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await simulateStep(pathway, step, complete));
      onRan();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside
      aria-label="Local test drive"
      className="mb-4 rounded-[var(--radius-card)] border-2 border-dashed border-info/60 bg-[#eef4ff] p-4 text-[13.5px] print:hidden"
      style={{ borderColor: "#2f6fdb88" }}
    >
      <div className="mb-1 flex items-center gap-2">
        <span className="rounded-[6px] bg-[#2f6fdb] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-white">
          Local only
        </span>
        <b>Test drive this step</b>
      </div>
      <p className="m-0 mb-3 text-text-muted">
        Fills in everything this step asks for with sample answers, through the real checks. Use it
        to see what a finished step looks like. Participants never see this panel.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => void run(false)}>
          {busy ? "Working..." : "Fill in sample answers"}
        </Button>
        {!done ? (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void run(true)}>
            Fill in and complete the step
          </Button>
        ) : null}
      </div>
      {result ? (
        <div className="mt-3">
          <b>{result.message}</b>
          <ul className="m-0 mt-1 pl-5">
            {result.done.map((item) => (
              <li key={item}>✓ {item}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-negative">
          {error}
        </p>
      ) : null}
    </aside>
  );
}
